import React, { useEffect, useState } from 'react'
import { formatDateTime } from '@/utils/dateFormat'
import { Modal } from '@/components/ui/Modal'
import { DataTable } from '@/components/ui/DataTable'
import {
  AccessGroup,
  AccessGroupCreatePayload,
  AvailableRole,
  createAccessGroup,
  deleteAccessGroup,
  getAccessGroups,
  getAvailableRoles,
  updateAccessGroup,
} from '@/api/accessGroups'

const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

interface FormState {
  entra_object_id: string
  display_name: string
  description: string
  roles: string[]
}

const EMPTY_FORM: FormState = {
  entra_object_id: '',
  display_name: '',
  description: '',
  roles: [],
}

const AccessSettings: React.FC = () => {
  const [groups, setGroups] = useState<AccessGroup[]>([])
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<AccessGroup | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const loadGroups = async () => {
    setLoading(true)
    setError(null)
    try {
      const [groupsResp, rolesResp] = await Promise.all([
        getAccessGroups({ page: 1, pageSize: 100 } as any),
        getAvailableRoles(),
      ])
      setGroups(groupsResp.items)
      setAvailableRoles(rolesResp)
    } catch (e: any) {
      setError(e?.message || 'Failed to load access groups')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadGroups()
  }, [])

  const openAdd = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setFormError(null)
    setModalOpen(true)
  }

  const openEdit = (group: AccessGroup) => {
    setEditing(group)
    setForm({
      entra_object_id: group.entra_object_id,
      display_name: group.display_name,
      description: group.description ?? '',
      roles: group.roles || [],
    })
    setFormError(null)
    setModalOpen(true)
  }

  const toggleRole = (role: string) => {
    setForm((f) => ({
      ...f,
      roles: f.roles.includes(role)
        ? f.roles.filter((r) => r !== role)
        : [...f.roles, role],
    }))
  }

  const validate = (): string | null => {
    if (!form.display_name.trim()) return 'Display name is required'
    if (!editing && !GUID_RE.test(form.entra_object_id.trim())) {
      return 'Entra Object ID must be a valid GUID'
    }
    return null
  }

  const handleSave = async () => {
    const v = validate()
    if (v) {
      setFormError(v)
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      if (editing) {
        await updateAccessGroup(editing.id, {
          display_name: form.display_name.trim(),
          description: form.description.trim() || null,
          roles: form.roles,
        })
      } else {
        const payload: AccessGroupCreatePayload = {
          entra_object_id: form.entra_object_id.trim().toLowerCase(),
          display_name: form.display_name.trim(),
          description: form.description.trim() || null,
          roles: form.roles,
        }
        await createAccessGroup(payload)
      }
      setModalOpen(false)
      await loadGroups()
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail || e?.message || 'Failed to save access group'
      setFormError(detail)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (group: AccessGroup) => {
    if (
      !window.confirm(
        `Deactivate access group "${group.display_name}"? Members will lose the roles this group grants.`
      )
    )
      return
    try {
      await deleteAccessGroup(group.id)
      await loadGroups()
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail || e?.message || 'Failed to deactivate access group'
      setError(detail)
    }
  }

  const roleLabel = (value: string): string => {
    const found = availableRoles.find((r) => r.value === value)
    return found?.label || value
  }

  return (
    <div>
      <h2
        style={{
          fontSize: '18px',
          fontWeight: 600,
          margin: '0 0 8px 0',
          color: 'var(--color-black)',
        }}
      >
        Access groups
      </h2>
      <p
        style={{
          fontSize: '13px',
          color: 'var(--color-dark-grey)',
          margin: '0 0 24px 0',
          maxWidth: '720px',
        }}
      >
        Register Entra (Azure AD) security groups here and assign them platform
        roles. Members are provisioned from Entra automatically — no action
        required. The group flagged with <strong>Platform Administrator</strong>{' '}
        controls who can change platform settings and manage Companies, Users,
        and Projects.
      </p>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '16px',
        }}
      >
        <button
          onClick={openAdd}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-magenta)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          + Add access group
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: '10px 12px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      <DataTable
        columns={[
          {
            key: 'display_name',
            header: 'Group',
            render: (_v: unknown, row: AccessGroup) => (
              <div>
                <div style={{ fontWeight: 500 }}>{row.display_name}</div>
                {row.description && (
                  <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                    {row.description}
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'entra_object_id',
            header: 'Entra Object ID',
            render: (value: string) => (
              <code style={{ fontSize: '12px' }}>{value}</code>
            ),
          },
          {
            key: 'roles',
            header: 'Roles',
            render: (_v: unknown, row: AccessGroup) => (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {(row.roles || []).map((r) => (
                  <span
                    key={r}
                    style={{
                      padding: '2px 8px',
                      backgroundColor:
                        r === 'platform_admin' ? '#fce7f3' : '#e0e7ff',
                      color:
                        r === 'platform_admin' ? '#9d174d' : '#3730a3',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    {roleLabel(r)}
                  </span>
                ))}
                {(!row.roles || row.roles.length === 0) && (
                  <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                    —
                  </span>
                )}
              </div>
            ),
          },
          {
            key: 'last_synced_at',
            header: 'Members',
            render: (_v: unknown, row: AccessGroup) => (
              <div style={{ fontSize: '12px' }}>
                {row.last_sync_member_count ?? '—'}
                {row.last_synced_at && (
                  <div style={{ color: 'var(--color-dark-grey)', fontSize: '11px' }}>
                    synced {formatDateTime(row.last_synced_at)}
                  </div>
                )}
                {row.last_sync_error && (
                  <div style={{ color: '#b91c1c', fontSize: '11px' }}>
                    {row.last_sync_error}
                  </div>
                )}
              </div>
            ),
          },
        ]}
        data={groups}
        rowKey="id"
        isLoading={loading}
        emptyMessage="No access groups defined yet."
        actions={[
          { label: 'Edit', onClick: (row: AccessGroup) => openEdit(row) },
          {
            label: 'Delete',
            onClick: (row: AccessGroup) => handleDelete(row),
            variant: 'danger' as const,
          },
        ]}
      />

      <Modal
        isOpen={modalOpen}
        title={editing ? 'Edit access group' : 'Add access group'}
        onClose={() => setModalOpen(false)}
        isDirty={form.display_name !== '' || form.entra_object_id !== '' || form.description !== '' || form.roles.length > 0}
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setModalOpen(false)}
              disabled={saving}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                backgroundColor: 'var(--color-white)',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '8px 16px',
                backgroundColor: saving ? 'var(--color-light-grey)' : 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add group'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '16px' }}>
          {formError && (
            <div
              style={{
                padding: '10px 12px',
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                borderRadius: '6px',
                fontSize: '13px',
              }}
            >
              {formError}
            </div>
          )}

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '4px',
              }}
            >
              Display name
            </label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="e.g. The Lab - Platform Administrator"
              style={inputStyle}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '4px',
              }}
            >
              Entra Object ID
            </label>
            <input
              type="text"
              value={form.entra_object_id}
              onChange={(e) =>
                setForm({ ...form, entra_object_id: e.target.value })
              }
              placeholder="ca8c1878-be9d-45b3-96c6-30733f2604e2"
              disabled={!!editing}
              style={{
                ...inputStyle,
                fontFamily: 'monospace',
                backgroundColor: editing ? '#f3f4f6' : 'var(--color-white)',
              }}
            />
            {editing && (
              <div
                style={{
                  fontSize: '11px',
                  color: 'var(--color-dark-grey)',
                  marginTop: '4px',
                }}
              >
                The backing Entra group cannot be changed after creation.
              </div>
            )}
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '4px',
              }}
            >
              Description (optional)
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '4px',
              }}
            >
              Roles
            </label>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                padding: '12px',
              }}
            >
              {availableRoles.length === 0 && (
                <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                  No roles available.
                </div>
              )}
              {availableRoles.map((role) => (
                <label
                  key={role.value}
                  style={{
                    display: 'flex',
                    gap: '10px',
                    cursor: 'pointer',
                    alignItems: 'flex-start',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.roles.includes(role.value)}
                    onChange={() => toggleRole(role.value)}
                    style={{ marginTop: '3px' }}
                  />
                  <span>
                    <span style={{ fontSize: '14px', fontWeight: 500 }}>
                      {role.label}
                    </span>
                    {role.description && (
                      <span
                        style={{
                          display: 'block',
                          fontSize: '12px',
                          color: 'var(--color-dark-grey)',
                        }}
                      >
                        {role.description}
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
  boxSizing: 'border-box',
}

export default AccessSettings
